// Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useReactToPrint } from 'react-to-print';
import $ from 'jquery';
import 'datatables.net';
import 'datatables.net-bs4/css/dataTables.bootstrap4.min.css';
import 'datatables.net-dt/js/dataTables.dataTables';
import Header from './Header';
import {
  Download,
  Upload,
  FileText,
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Building2,
  Home,
  CreditCard,
  Filter,
  Search,
  RefreshCw,
  Eye,
  Edit,
  Trash2,
  Share2,
  Mail,
  Printer,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowUpRight,
  ArrowDownRight,
  ChevronDown,
  ChevronUp,
  MoreVertical,
  DownloadCloud,
  UploadCloud,
  PieChart as PieChartIcon,
  LineChart,
  Layers,
  Target,
  Award,
  Shield,
  Lock,
  Key,
  Calculator,
  Smartphone,
  Globe,
  Cloud,
  Database,
  Server,
  Cpu,
  ShieldCheck, Landmark
} from 'lucide-react';

const Dashboard = () => {
  const server_app_api_php = window.SERVER_APP_API_PHP;
  const userType = localStorage.getItem('userType') || 'Admin';
  const userEmail = localStorage.getItem('email') || '';
  const userCompanyID = localStorage.getItem('companyID') || 1;
  const userId = localStorage.getItem('userID') || 1;

  // State management
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPlots: 0,
    totalHouses: 0,
    totalCustomers: 0,
    totalRevenue: 0,
    monthlyGrowth: 0,
    activeContracts: 0,
    pendingPayments: 0,
    expenses: 0
  });

  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDateRange, setSelectedDateRange] = useState('this_month');
  const [showFilters, setShowFilters] = useState(false);
  const [notification, setNotification] = useState(null);
  const [businessLocations, setBusinessLocations] = useState([]);
  const [businessLocationFilter, setBusinessLocationFilter] = useState('all');

  // Data states
  const [plotsDetailedData, setPlotsDetailedData] = useState([]);
  const [housesDetailedData, setHousesDetailedData] = useState([]);
  const [plotsExpensesData, setPlotsExpensesData] = useState([]);
  const [housesExpensesData, setHousesExpensesData] = useState([]);
  const [customerStatement, setCustomerStatement] = useState(null);
  const [tenantStatement, setTenantStatement] = useState(null);

  const componentRef = useRef();

  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-ZM', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Fetch initial data
  useEffect(() => {
    fetchDashboardData();
    fetchBusinessLocations();
    
    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchDashboardData, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch stats
      const statsResponse = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'getDashboardStats',
        companyID: userCompanyID
      });

      if (statsResponse.data) {
        setStats(statsResponse.data);
      }

      // Fetch detailed data based on user type
      if (userType !== 'Customer' && userType !== 'Tenant') {
        await fetchPlotsDetailedStatement();
        await fetchHousesDetailedStatement();
        await fetchPlotsExpenses();
        await fetchHousesExpenses();
      } else if (userType === 'Customer') {
        await fetchCustomerStatement();
      } else if (userType === 'Tenant') {
        await fetchTenantStatement();
      }

      showNotification('Data updated successfully', 'success');
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      showNotification('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessLocations = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'getAllBusinessLocations',
        companyID: userCompanyID
      });
      setBusinessLocations(response.data);
    } catch (error) {
      console.error('Error fetching business locations:', error);
    }
  };

  const fetchPlotsDetailedStatement = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'fetchPlotsDetailedStatement'
      });
      setPlotsDetailedData(response.data);
    } catch (error) {
      console.error('Error fetching plots detailed statement:', error);
    }
  };

  const fetchHousesDetailedStatement = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'fetchHousesDetailedStatement'
      });
      setHousesDetailedData(response.data);
    } catch (error) {
      console.error('Error fetching houses detailed statement:', error);
    }
  };

  const fetchPlotsExpenses = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'fetchPlotsBriefExpensesStatement',
        userCompanyID: userCompanyID
      });
      setPlotsExpensesData(response.data);
    } catch (error) {
      console.error('Error fetching plots expenses:', error);
    }
  };

  const fetchHousesExpenses = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'fetchHousesBriefExpensesStatement',
        userCompanyID: userCompanyID
      });
      setHousesExpensesData(response.data);
    } catch (error) {
      console.error('Error fetching houses expenses:', error);
    }
  };

  const fetchCustomerStatement = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'getPlotCustomer',
        userEmail: userEmail
      });
      setCustomerStatement(response.data);
    } catch (error) {
      console.error('Error fetching customer statement:', error);
    }
  };

  const fetchTenantStatement = async () => {
    try {
      const response = await axios.post(`${server_app_api_php}/server/index.php`, {
        apiType: 'getHouseTenant',
        userEmail: userEmail
      });
      setTenantStatement(response.data);
    } catch (error) {
      console.error('Error fetching tenant statement:', error);
    }
  };

  // Export functions
  const exportToPDF = (data, title, columns) => {
    const doc = new jsPDF();
    doc.text(title, 20, 20);
    
    const tableData = data.map(item => 
      columns.map(col => item[col.key])
    );
    
    doc.autoTable({
      head: [columns.map(col => col.label)],
      body: tableData,
      startY: 30,
      theme: 'striped'
    });
    
    doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
  };

  const exportToCSV = (data, filename) => {
    const csvData = JSON.stringify(data);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  // Handle print
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: 'RealEstatePro_Report'
  });

  // Dashboard Stats Cards
  const StatsCard = ({ title, value, icon: Icon, change, color, onClick }) => {
    const colorClasses = {
      blue: 'from-blue-500 to-blue-600',
      purple: 'from-purple-500 to-purple-600',
      green: 'from-green-500 to-emerald-600',
      orange: 'from-orange-500 to-amber-600'
    };

    return (
      <div 
        className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow duration-300 cursor-pointer"
        onClick={onClick}
      >
        <div className="flex items-center justify-between mb-4">
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color]} bg-opacity-10`}>
            <Icon className={`w-6 h-6 ${colorClasses[color].includes('blue') ? 'text-blue-600' : 
                            colorClasses[color].includes('purple') ? 'text-purple-600' :
                            colorClasses[color].includes('green') ? 'text-green-600' : 'text-orange-600'}`} />
          </div>
          {change && (
            <div className={`flex items-center text-sm font-medium ${change > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {change > 0 ? <ArrowUpRight className="w-4 h-4 mr-1" /> : <ArrowDownRight className="w-4 h-4 mr-1" />}
              {Math.abs(change)}%
            </div>
          )}
        </div>
        <h3 className="text-3xl font-bold text-gray-900 mb-2">{value}</h3>
        <p className="text-gray-600 text-sm">{title}</p>
      </div>
    );
  };

  // Data Table Component
  const DataTable = ({ title, data, columns, actions, onExport }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [entries, setEntries] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    const filteredData = data.filter(item =>
      columns.some(col =>
        String(item[col.key]).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    const paginatedData = filteredData.slice(
      (currentPage - 1) * entries,
      currentPage * entries
    );

    return (
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <p className="text-gray-600 text-sm">{filteredData.length} records found</p>
            </div>
            <div className="flex items-center space-x-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <button
                onClick={onExport}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <Download className="w-5 h-5" />
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {column.label}
                  </th>
                ))}
                {actions && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedData.map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {column.format ? column.format(row[column.key]) : row[column.key]}
                    </td>
                  ))}
                  {actions && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        {actions(row)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-700">
                Showing {(currentPage - 1) * entries + 1} to {Math.min(currentPage * entries, filteredData.length)} of {filteredData.length} entries
              </span>
              <select
                className="border border-gray-300 rounded-lg px-3 py-1 text-sm"
                value={entries}
                onChange={(e) => setEntries(Number(e.target.value))}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-3 py-1 bg-blue-600 text-white rounded-lg">{currentPage}</span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage * entries >= filteredData.length}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Main Content */}
      <main className="lg:pl-64 pt-16">
        {/* Notification */}
        {notification && (
          <div className={`fixed top-20 right-6 z-50 px-6 py-3 rounded-lg shadow-lg transform transition-all duration-300 ${
            notification.type === 'success' ? 'bg-green-500' :
            notification.type === 'error' ? 'bg-red-500' :
            'bg-blue-500'
          } text-white`}>
            <div className="flex items-center space-x-2">
              {notification.type === 'success' && <CheckCircle className="w-5 h-5" />}
              {notification.type === 'error' && <AlertCircle className="w-5 h-5" />}
              <span>{notification.message}</span>
            </div>
          </div>
        )}

        <div className="p-6">
          {/* Dashboard Header */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
                <p className="text-gray-600">Welcome back! Here's what's happening with your properties.</p>
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={fetchDashboardData}
                  className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-2"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                  <span>Refresh</span>
                </button>
                <button className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:shadow-lg transition-shadow flex items-center space-x-2">
                  <DownloadCloud className="w-5 h-5" />
                  <span>Generate Report</span>
                </button>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatsCard
              title="Total Plots"
              value={stats.totalPlots}
              icon={Landmark}
              change={12.5}
              color="blue"
              onClick={() => setActiveTab('plots')}
            />
            <StatsCard
              title="Total Houses"
              value={stats.totalHouses}
              icon={Home}
              change={8.2}
              color="purple"
              onClick={() => setActiveTab('houses')}
            />
            <StatsCard
              title="Total Revenue"
              value={formatCurrency(stats.totalRevenue)}
              icon={DollarSign}
              change={15.3}
              color="green"
              onClick={() => setActiveTab('revenue')}
            />
            <StatsCard
              title="Active Customers"
              value={stats.totalCustomers}
              icon={Users}
              change={5.7}
              color="orange"
              onClick={() => setActiveTab('customers')}
            />
          </div>

          {/* Tabs Navigation */}
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {[
                  { id: 'overview', label: 'Overview', icon: PieChartIcon },
                  { id: 'plots', label: 'Plots', icon: Landmark },
                  { id: 'houses', label: 'Houses', icon: Home },
                  { id: 'revenue', label: 'Revenue', icon: DollarSign },
                  { id: 'expenses', label: 'Expenses', icon: CreditCard },
                  { id: 'customers', label: 'Customers', icon: Users }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-3 px-1 inline-flex items-center space-x-2 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="w-5 h-5" />
                    <span>{tab.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activity */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
                    <button className="text-sm text-blue-600 hover:text-blue-800">View All</button>
                  </div>
                  <div className="space-y-4">
                    {[
                      { type: 'payment', message: 'Plot #A-102 payment received', time: '2 min ago', amount: 'ZMW 5,000' },
                      { type: 'contract', message: 'New rental contract signed', time: '1 hour ago', tenant: 'John Smith' },
                      { type: 'expense', message: 'Maintenance expense recorded', time: '3 hours ago', amount: 'ZMW 2,500' },
                      { type: 'payment', message: 'House #B-205 payment received', time: '5 hours ago', amount: 'ZMW 3,000' }
                    ].map((activity, index) => (
                      <div key={index} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${
                          activity.type === 'payment' ? 'bg-green-100 text-green-600' :
                          activity.type === 'contract' ? 'bg-blue-100 text-blue-600' :
                          'bg-orange-100 text-orange-600'
                        }`}>
                          {activity.type === 'payment' ? <DollarSign className="w-4 h-4" /> :
                           activity.type === 'contract' ? <FileText className="w-4 h-4" /> :
                           <CreditCard className="w-4 h-4" />}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">{activity.message}</p>
                          <p className="text-xs text-gray-500">{activity.time}</p>
                        </div>
                        {activity.amount && (
                          <div className="text-sm font-semibold text-gray-900">{activity.amount}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Quick Actions</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <button className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl hover:shadow-md transition-shadow text-left">
                      <CreditCard className="w-8 h-8 text-blue-600 mb-2" />
                      <p className="font-medium text-gray-900">Record Payment</p>
                      <p className="text-sm text-gray-600">Add new payment</p>
                    </button>
                    <button className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl hover:shadow-md transition-shadow text-left">
                      <FileText className="w-8 h-8 text-purple-600 mb-2" />
                      <p className="font-medium text-gray-900">Generate Report</p>
                      <p className="text-sm text-gray-600">Create statement</p>
                    </button>
                    <button className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-xl hover:shadow-md transition-shadow text-left">
                      <Users className="w-8 h-8 text-green-600 mb-2" />
                      <p className="font-medium text-gray-900">Add Client</p>
                      <p className="text-sm text-gray-600">New customer</p>
                    </button>
                    <button className="p-4 bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl hover:shadow-md transition-shadow text-left">
                      <Calculator className="w-8 h-8 text-orange-600 mb-2" />
                      <p className="font-medium text-gray-900">Calculate</p>
                      <p className="text-sm text-gray-600">Mortgage tool</p>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'plots' && userType !== 'Customer' && userType !== 'Tenant' && (
              <DataTable
                title="Plots Detailed Statement"
                data={plotsDetailedData}
                columns={[
                  { key: 'plotNumber', label: 'Plot Number', format: (val) => <span className="font-semibold">{val}</span> },
                  { key: 'customerName', label: 'Customer Name' },
                  { key: 'amountPaid', label: 'Amount Paid', format: formatCurrency },
                  { key: 'datePaid', label: 'Date Paid', format: formatDate },
                  { key: 'status', label: 'Status', format: (val) => (
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      val === 'paid' ? 'bg-green-100 text-green-800' :
                      val === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {val}
                    </span>
                  )}
                ]}
                actions={(row) => (
                  <>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Edit className="w-4 h-4 text-blue-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </button>
                  </>
                )}
                onExport={() => exportToPDF(plotsDetailedData, 'Plots_Detailed_Statement', [
                  { key: 'plotNumber', label: 'Plot Number' },
                  { key: 'customerName', label: 'Customer Name' },
                  { key: 'amountPaid', label: 'Amount Paid' },
                  { key: 'datePaid', label: 'Date Paid' }
                ])}
              />
            )}

            {activeTab === 'houses' && userType !== 'Customer' && userType !== 'Tenant' && (
              <DataTable
                title="Houses Detailed Statement"
                data={housesDetailedData}
                columns={[
                  { key: 'houseNumber', label: 'House Number' },
                  { key: 'block', label: 'Block' },
                  { key: 'tenantName', label: 'Tenant Name' },
                  { key: 'amountPaid', label: 'Amount Paid', format: formatCurrency },
                  { key: 'monthPaid', label: 'Month' }
                ]}
                actions={(row) => (
                  <>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Eye className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-1 hover:bg-gray-100 rounded">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </button>
                  </>
                )}
                onExport={() => exportToPDF(housesDetailedData, 'Houses_Detailed_Statement', [
                  { key: 'houseNumber', label: 'House Number' },
                  { key: 'block', label: 'Block' },
                  { key: 'tenantName', label: 'Tenant Name' },
                  { key: 'amountPaid', label: 'Amount Paid' },
                  { key: 'monthPaid', label: 'Month' }
                ])}
              />
            )}

            {activeTab === 'expenses' && userType !== 'Customer' && userType !== 'Tenant' && (
              <div className="space-y-6">
                <DataTable
                  title="Plots Expenses"
                  data={plotsExpensesData}
                  columns={[
                    { key: 'expenseType', label: 'Expense Type' },
                    { key: 'amount', label: 'Amount', format: formatCurrency },
                    { key: 'date', label: 'Date', format: formatDate },
                    { key: 'location', label: 'Location' },
                    { key: 'status', label: 'Status', format: (val) => (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        val === 'paid' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {val}
                      </span>
                    )}
                  ]}
                  onExport={() => exportToCSV(plotsExpensesData, 'plots_expenses')}
                />

                <DataTable
                  title="Houses Expenses"
                  data={housesExpensesData}
                  columns={[
                    { key: 'expenseType', label: 'Expense Type' },
                    { key: 'amount', label: 'Amount', format: formatCurrency },
                    { key: 'date', label: 'Date', format: formatDate },
                    { key: 'location', label: 'Location' },
                    { key: 'status', label: 'Status', format: (val) => (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        val === 'paid' ? 'bg-green-100 text-green-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {val}
                      </span>
                    )}
                  ]}
                  onExport={() => exportToCSV(housesExpensesData, 'houses_expenses')}
                />
              </div>
            )}

            {/* Customer/Tenant View */}
            {(userType === 'Customer' || userType === 'Tenant') && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {userType === 'Customer' ? 'Customer Statement' : 'Tenant Statement'}
                    </h3>
                    <p className="text-gray-600">Your payment history and account details</p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handlePrint}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
                    >
                      <Printer className="w-5 h-5" />
                      <span>Print</span>
                    </button>
                    <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2">
                      <Download className="w-5 h-5" />
                      <span>Download PDF</span>
                    </button>
                  </div>
                </div>

                {/* Statement Content */}
                {customerStatement && userType === 'Customer' && (
                  <div className="space-y-6">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl">
                        <p className="text-sm text-gray-600">Total Paid</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(customerStatement.totalAmountPaid || 0)}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl">
                        <p className="text-sm text-gray-600">Balance</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatCurrency(customerStatement.balance || 0)}
                        </p>
                      </div>
                      <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl">
                        <p className="text-sm text-gray-600">Last Payment</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {formatDate(customerStatement.lastDatePaid || 'N/A')}
                        </p>
                      </div>
                    </div>

                    {/* Payment History */}
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h4>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {customerStatement.payments?.map((payment, index) => (
                              <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap">{formatDate(payment.date)}</td>
                                <td className="px-6 py-4 whitespace-nowrap font-semibold">
                                  {formatCurrency(payment.amount)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">{payment.description}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                                    Paid
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {tenantStatement && userType === 'Tenant' && (
                  <div className="space-y-6">
                    {/* Similar structure for tenant statement */}
                    <div className="text-center py-8">
                      <Home className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Loading tenant statement...</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Quick Stats Bottom Bar */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Pending Actions</p>
                  <p className="text-2xl font-bold mt-1">{stats.pendingPayments || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 opacity-80" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Active Contracts</p>
                  <p className="text-2xl font-bold mt-1">{stats.activeContracts || 0}</p>
                </div>
                <FileText className="w-8 h-8 opacity-80" />
              </div>
            </div>
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-2xl p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm opacity-90">Monthly Expenses</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(stats.expenses || 0)}</p>
                </div>
                <CreditCard className="w-8 h-8 opacity-80" />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Print Component */}
      <div style={{ display: 'none' }}>
        <div ref={componentRef}>
          <h1>RealEstatePro Report</h1>
          <p>Generated on {new Date().toLocaleDateString()}</p>
          {/* Report content will be added here */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;